import { KAPLAYCtx } from "kaplay";
import { GAME_HEIGHT, GAME_WIDTH, SCENES, SHAFT_LEFT, SHAFT_RIGHT } from "../constants";
import { getHighScore } from "../storage";
import { COLORS } from "../colors";
import { MIX_RECIPES } from "../rules/colorMixing";
import { PALETTE, textStyle } from "../theme";
import { SECONDARY_COLORS } from "../types";
import { addPulsingButton } from "../ui/buttons";
import { rainbowRgb } from "../ui/rainbow";

const CENTER_X = GAME_WIDTH / 2;

export function registerStartScene(k: KAPLAYCtx): void {
    k.scene(SCENES.start, () => {
        k.add([k.rect(GAME_WIDTH, GAME_HEIGHT), k.pos(0, 0), k.color(15, 17, 26), k.z(-10)]);

        // Faint shaft walls behind the menu
        for (const wallX of [SHAFT_LEFT, SHAFT_RIGHT - 3]) {
            k.add([k.rect(3, GAME_HEIGHT), k.pos(wallX, 0), k.color(40, 45, 65), k.opacity(0.6), k.z(-5)]);
        }

        const laser = k.add([k.rect(GAME_WIDTH, 10), k.pos(0, 70), k.color(255, 50, 100), k.z(5)]);
        laser.onUpdate(() => {
            laser.color = k.rgb(...rainbowRgb(k.time(), 4));
        });

        k.add([k.text("COLOURFALLS", textStyle(42)), k.pos(CENTER_X, 130), k.anchor("center"), k.color(...PALETTE.white)]);
        k.add([
            k.text("RHYTHM COLOR-MATCHING ACTION", textStyle(15)),
            k.pos(CENTER_X, 175),
            k.anchor("center"),
            k.color(140, 170, 220),
        ]);
        k.add([
            k.text(`BEST SCORE: ${getHighScore()}`, textStyle(16)),
            k.pos(CENTER_X, 215),
            k.anchor("center"),
            k.color(...PALETTE.gold),
        ]);

        addHowToPlayDemo(k);
        addMixingGuide(k);

        const startGame = () => k.go(SCENES.game);
        addPulsingButton(k, { label: "START GAME", y: 730, pulseAmount: 0.025, onPress: startGame });
        k.onKeyPress(["space", "enter"], startGame);

        k.add([
            k.text("TOUCH BUTTONS OR KEYS (1, 2, 3 / R, B, Y)", textStyle(13)),
            k.pos(CENTER_X, 850),
            k.anchor("center"),
            k.color(120, 130, 155),
        ]);
    });
}

/** A purple cube above a purple barrier, with the match/mismatch rule. */
function addHowToPlayDemo(k: KAPLAYCtx): void {
    const demoRgb = COLORS.purple.rgb;

    k.add([
        k.rect(360, 200, { radius: 8 }),
        k.pos(CENTER_X, 340),
        k.anchor("center"),
        k.color(22, 26, 40),
        k.outline(2, k.rgb(60, 70, 100)),
    ]);
    k.add([
        k.rect(36, 36, { radius: 6 }),
        k.pos(CENTER_X, 290),
        k.anchor("center"),
        k.color(...demoRgb),
        k.outline(2, k.rgb(...PALETTE.white)),
    ]);
    k.add([k.rect(280, 18, { radius: 4 }), k.pos(CENTER_X, 350), k.anchor("center"), k.color(...demoRgb), k.opacity(0.85)]);

    k.add([
        k.text("MATCH COLOR -> FALL THROUGH", textStyle(13)),
        k.pos(CENTER_X, 395),
        k.anchor("center"),
        k.color(100, 240, 150),
    ]);
    k.add([
        k.text("MISMATCH -> LIFTED TO LASER!", textStyle(13)),
        k.pos(CENTER_X, 420),
        k.anchor("center"),
        k.color(255, 90, 90),
    ]);
}

/** One line per secondary colour, built from MIX_RECIPES. */
function addMixingGuide(k: KAPLAYCtx): void {
    k.add([
        k.rect(420, 160, { radius: 8 }),
        k.pos(CENTER_X, 545),
        k.anchor("center"),
        k.color(22, 26, 40),
        k.outline(2, k.rgb(60, 70, 100)),
    ]);
    k.add([k.text("COLOR MIXING GUIDE", textStyle(15)), k.pos(CENTER_X, 485), k.anchor("center"), k.color(...PALETTE.white)]);

    SECONDARY_COLORS.forEach((secondary, index) => {
        const [first, second] = MIX_RECIPES[secondary].map((c) => COLORS[c].name);
        const rgb = COLORS[secondary].rgb;
        const y = 525 + index * 30;

        k.add([k.rect(14, 14, { radius: 3 }), k.pos(120, y), k.anchor("center"), k.color(...rgb)]);
        k.add([k.text(`${first} + ${second} = ${COLORS[secondary].name}`, textStyle(14)), k.pos(140, y - 7), k.color(...rgb)]);
    });

    k.add([
        k.text("Double-tap any button to reset to primary", textStyle(12)),
        k.pos(CENTER_X, 612),
        k.anchor("center"),
        k.color(160, 170, 190),
    ]);
}
